import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button, 
  Card, 
  CardActions, 
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  FormHelperText,
  Grid
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Business } from '../../../types/UserTypes';
import axios from '../../../../services/ApiService';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';

function BusinessEditBasicDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = (location.state as { userId?: number })?.userId;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [businessData, setBusinessData] = useState<Business>({
    businessId: 0,
    userId: 0,
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    contact: '',
    businessABN: '',
    legalName: '',
    tradeName: '',
    address: '',
    webLink: '',
    logoId: 0,
    documentId: 0,
    status: BVTConstants.ACTIVE,
    agentCompanyId: 0,
    agentCompanyName: '',
    assignments: [],
    serviceId: 0,
    sectorId: 0,
    businessType: '',
    businessSize: '',
    acnIcn: '',
    grossIncome: '',
    grossAssets: '',
    employees: '',
    supportRequired: [],
    createdAt: '',
    updatedAt: '',
    lastLoginAt: '',
  });

  useEffect(() => {
    fetchBusinessData();
  }, []);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      const response = await axios.post("/getBusinessByUserId", userId);
      setBusinessData(response.data);
    } catch (error) {
      console.error('Error fetching business data.');
      toast.error("Failed to fetch business data");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setBusinessData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    let newErrors: { [key: string]: string } = {};

    if (!businessData.legalName.trim()) {
      newErrors.legalName = 'Legal name is required';
    }
    if (!businessData.tradeName.trim()) {
      newErrors.tradeName = 'Trade name is required';
    }
    if (!businessData.businessABN.trim()) {
      newErrors.businessABN = 'ABN is required';
    } else if (!/^\d{11}$/.test(businessData.businessABN.replace(/\s/g, ''))) {
      newErrors.businessABN = 'Please enter a valid 11-digit ABN';
    }
    if (!businessData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!businessData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!businessData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(businessData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!businessData.contact.trim()) {
      newErrors.contact = 'Phone number is required';
    } else {
      if (!BVTConstants.PHONE_VALIDATION_PATTERN.test(businessData.contact.trim())) {
        newErrors.contact = 'Please enter a valid phone number';
      }
      if (businessData.contact.trim().length < BVTConstants.PHONE_MIN_LENGTH || 
          businessData.contact.trim().length > BVTConstants.PHONE_MAX_LENGTH) {
        newErrors.contact = `Phone number must be between ${BVTConstants.PHONE_MIN_LENGTH}-${BVTConstants.PHONE_MAX_LENGTH} characters`;
      }
    }
    if (!businessData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await axios.post("/updateBusinessBaseProfile", businessData);
      toast.success("Business updated successfully");
      navigate(-1);
    } catch (error) {
      console.error('Error updating business.');
      toast.error("Failed to update business");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>BVT Admin - Edit Business</title></Helmet>
      <Stack spacing={3}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Card>
            <CardHeader title="Edit Base Profile" />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.legalName}>
                    <InputLabel>Legal Name</InputLabel>
                    <OutlinedInput 
                      label="Legal Name" 
                      name="legalName"
                      value={businessData.legalName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 100 }}
                    />
                    {errors.legalName && (
                      <FormHelperText error>{errors.legalName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.tradeName}>
                    <InputLabel>Trade Name</InputLabel>
                    <OutlinedInput 
                      label="Trade Name" 
                      name="tradeName"
                      value={businessData.tradeName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 100 }}
                    />
                    {errors.tradeName && (
                      <FormHelperText error>{errors.tradeName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.businessABN}>
                    <InputLabel>ABN</InputLabel>
                    <OutlinedInput 
                      label="ABN" 
                      name="businessABN"
                      value={businessData.businessABN}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        setBusinessData(prev => ({
                          ...prev,
                          businessABN: value
                        }));
                        if (errors.businessABN) {
                          setErrors(prev => ({ ...prev, businessABN: '' }));
                        }
                      }}
                      inputProps={{ maxLength: 11 }}
                      placeholder="11 digits"
                    />
                    {errors.businessABN && (
                      <FormHelperText error>{errors.businessABN}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.address}>
                    <InputLabel>Address</InputLabel>
                    <OutlinedInput 
                      label="Address" 
                      name="address"
                      value={businessData.address}
                      onChange={handleChange}
                      inputProps={{ maxLength: 200 }}
                    />
                    {errors.address && (
                      <FormHelperText error>{errors.address}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.firstName}>
                    <InputLabel>Contact Person First name</InputLabel>
                    <OutlinedInput 
                      label="Contact Person First name" 
                      name="firstName"
                      value={businessData.firstName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 100 }}
                    />
                    {errors.firstName && (
                      <FormHelperText error>{errors.firstName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.lastName}>
                    <InputLabel>Contact Person Last name</InputLabel>
                    <OutlinedInput 
                      label="Contact Person Last name" 
                      name="lastName"
                      value={businessData.lastName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 100 }}
                    />
                    {errors.lastName && (
                      <FormHelperText error>{errors.lastName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.email}>
                    <InputLabel>Email address</InputLabel>
                    <OutlinedInput 
                      label="Email address" 
                      name="email"
                      value={businessData.email}
                      onChange={handleChange}
                      inputProps={{ maxLength: 100 }}
                    />
                    {errors.email && (
                      <FormHelperText error>{errors.email}</FormHelperText>
                    )}
                    <FormHelperText sx={{ color: 'warning.main' }}>
                      Changing email id will also update the login ID for this user
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item md={6} xs={12}>
                  <FormControl fullWidth required error={!!errors.contact}>
                    <InputLabel>Phone number</InputLabel>
                    <OutlinedInput 
                      label="Phone number" 
                      name="contact"
                      type="tel"
                      value={businessData.contact}
                      onChange={(e) => {
                        const value = e.target.value.replace(BVTConstants.PHONE_INPUT_PATTERN, '');
                        setBusinessData(prev => ({
                          ...prev,
                          contact: value
                        }));
                        if (errors.contact) {
                          setErrors(prev => ({ ...prev, contact: '' }));
                        }
                      }}
                      inputProps={{ maxLength: BVTConstants.PHONE_MAX_LENGTH }}
                      placeholder="10-15 digits"
                    />
                    {errors.contact && (
                      <FormHelperText error>{errors.contact}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
              <Button variant="contained" type="submit">Update</Button>
            </CardActions>
          </Card>
        </form>
      </Stack>
      <LoadingSpinner loading={loading} />
    </>
  );
}

export default BusinessEditBasicDetails; 